/**
 * Xuất PDF thời khóa biểu từ dữ liệu (vector), không phụ thuộc kích thước màn hình / html2canvas.
 * Font Noto Sans tải runtime (CDN) để hiển thị tiếng Việt.
 */
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const NOTO_TTF_URL =
  "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf";

const FONT_NAME = "NotoSans";

/** day API: 1=T2 … 7=CN */
const DAY_LABELS = ["", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7", "Chủ nhật"];

let fontBase64Promise = null;

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadNotoFontBase64() {
  if (!fontBase64Promise) {
    fontBase64Promise = fetch(NOTO_TTF_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Font HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(arrayBufferToBase64);
  }
  return fontBase64Promise;
}

function pad2(n) {
  return String(n ?? "").padStart(2, "0");
}

function sortEvents(events) {
  return [...(events || [])].sort((a, b) => {
    const d = (a.day || 0) - (b.day || 0);
    if (d !== 0) return d;
    const s = (a.start || 0) - (b.start || 0);
    if (s !== 0) return s;
    return String(a.class || "").localeCompare(String(b.class || ""), "vi");
  });
}

function resolveFilterLabel(options, id, allLabel) {
  if (!id || id === "all") return allLabel;
  const found = (options || []).find((x) => x.id === id);
  return found?.name || String(id);
}

/**
 * @param {object} params
 * @param {Array} params.events — đã lọc (vd. filteredData)
 * @param {object} params.filters — branch_id, subject_id, course_id, search, venue
 * @param {object} params.filterOptions — { branches, subjects, courses }
 * @param {string} params.periodLabel — ví dụ "THÁNG 06, 2026" hoặc khoảng tuần
 * @param {string} params.viewMode — month | week | day
 */
export async function exportSchedulePdf({
  events,
  filters,
  filterOptions,
  periodLabel,
  viewMode,
}) {
  let fontBase64;
  try {
    fontBase64 = await loadNotoFontBase64();
  } catch (e) {
    console.warn("Không tải được font Noto, dùng font mặc định (có thể lỗi dấu):", e);
    fontBase64 = null;
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  if (fontBase64) {
    doc.addFileToVFS("NotoSans-Regular.ttf", fontBase64);
    doc.addFont("NotoSans-Regular.ttf", FONT_NAME, "normal");
    doc.setFont(FONT_NAME, "normal");
  }

  const title = "Thời khóa biểu";
  const exportedAt = new Date().toLocaleString("vi-VN");

  const branch = resolveFilterLabel(
    filterOptions?.branches,
    filters?.branch_id,
    "Tất cả chi nhánh"
  );
  const subject = resolveFilterLabel(
    filterOptions?.subjects,
    filters?.subject_id,
    "Tất cả môn"
  );
  const course = resolveFilterLabel(
    filterOptions?.courses,
    filters?.course_id,
    "Tất cả khóa học"
  );
  const search = (filters?.search || "").trim() || "—";
  const venueRaw = String(filters?.venue || "center").toLowerCase();
  const venueLabel =
    venueRaw === "school" ? "Tại trường" : venueRaw === "all" ? "Tất cả (trung tâm + trường)" : "Trung tâm";

  doc.setFontSize(14);
  doc.text(title, 14, 16);

  doc.setFontSize(9);
  let y = 22;
  const lines = [
    `Kỳ hiển thị: ${periodLabel || "—"}`,
    `Chế độ: ${viewMode === "month" ? "Tháng" : viewMode === "week" ? "Tuần" : "Ngày"}`,
    `Địa điểm lớp: ${venueLabel}`,
    `Chi nhánh: ${branch}  |  Môn: ${subject}  |  Khóa: ${course}`,
    `Tìm kiếm: ${search}`,
    `Xuất lúc: ${exportedAt}`,
  ];
  lines.forEach((line) => {
    doc.text(line, 14, y);
    y += 5;
  });

  const rows = sortEvents(events).map((ev) => {
    const day = DAY_LABELS[ev.day] || String(ev.day ?? "—");
    const time = `${pad2(ev.start)}:00 – ${pad2(ev.end)}:00`;
    const validity =
      ev.start_date && ev.end_date
        ? `${ev.start_date} → ${ev.end_date}`
        : ev.end_date
          ? `→ ${ev.end_date}`
          : ev.start_date
            ? `${ev.start_date} →`
            : "—";
    return [
      day,
      time,
      ev.class || "—",
      ev.course || "—",
      ev.subject || "—",
      ev.teacher || "—",
      ev.room || "—",
      ev.branch || "—",
      String(ev.students ?? "0"),
      validity,
    ];
  });

  autoTable(doc, {
    startY: y + 4,
    head: [[
      "Thứ",
      "Giờ",
      "Lớp",
      "Khóa học",
      "Môn",
      "Giáo viên",
      "Phòng",
      "Cơ sở",
      "Sĩ số",
      "Hiệu lực",
    ]],
    body: rows.length
      ? rows
      : [["—", "—", "Không có dữ liệu theo bộ lọc", "—", "—", "—", "—", "—", "—", "—"]],
    styles: {
      font: fontBase64 ? FONT_NAME : "helvetica",
      fontSize: 7,
      cellPadding: 1.5,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [185, 28, 28],
      textColor: 255,
      font: fontBase64 ? FONT_NAME : "helvetica",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 22 },
      2: { cellWidth: 32 },
      3: { cellWidth: 36 },
      4: { cellWidth: 28 },
      5: { cellWidth: 28 },
      6: { cellWidth: 18 },
      7: { cellWidth: 28 },
      8: { cellWidth: 12 },
      9: { cellWidth: 28 },
    },
    margin: { left: 14, right: 14 },
  });

  const safeName = `ThoiKhoaBieu_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(safeName);
}
